import React, { useEffect, useState, useContext, useCallback } from 'react';
import './details.css';
import '../Components/modal.css';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from '../Components/Spinner';
import { ActionsIcon, DeleteIcon, DeviceIcon } from '../Icons';
import { BASE_URL_API, OTA_URL } from '../Constants';
import { useAuth } from '../hooks/AuthProvider';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import PauseIcon from '@mui/icons-material/PauseCircleOutlined';
import ResumeIcon from '@mui/icons-material/PlayCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
// import ReportIcon from '@mui/icons-material/MailOutlined';
import { ShipmentInfo } from './Shipments';
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Tooltip } from '@mui/material';
import { deleteShipment, controlShipment } from '../lib/actions';

import { LanguageContext } from '../context/LanguageContext';
import shipmentTranslations from '../languages/shipment.json';

import Alert from '../Components/Alert';
import { getErrorMessage } from '../utils/errorUtils';

export const ShipmentStatus = ({ state, setState }) => {
    const { lang } = useContext(LanguageContext);
    const t = shipmentTranslations[lang].shipmentDetails;
    return (
        <div id='modal' style={{ visibility: state.open ? 'visible' : 'hidden', transition: 'visibility .2s linear' }}
            onClick={() => setState({ ...state, open: false })}>
            <div id='side-popup' style={{ right: state.open ? 0 : '-40%', transition: 'right .2s linear' }}
                onClick={(e) => e.stopPropagation()}>
                <div className='modal-header'>
                    <h2>{state.name}  {t.statusUpdate}</h2>
                </div>
                <div className='modal-content' style={{ height: 'calc(100% - 80px)' }}>
                    {state.shipDetails.map((detail, idx) => (
                        <Tooltip key={idx} title={detail.status} placement='left'>
                            <div className='settings-row-action'
                                style={{ width: '100%', justifyContent: 'flex-start', padding: 8 }}>
                                <span style={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    border: `2px solid ${detail.fail ? 'rgb(236, 69, 78)' : 'var(--accent)'}`
                                }}></span>
                                <span style={{ fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))', color: 'var(--root-color)' }}>{detail.status}</span>
                                <span style={{ fontSize: 'calc(var(--ui-font-size) * var(--font-size-multiplier))' }}>{detail.time}</span>
                            </div>
                        </Tooltip>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const ShipmentDetails = () => {
    const { id } = useParams(), navigate = useNavigate();
    const { permissions } = useAuth();
    const [status, setStatus] = useState({
        open: false,
        name: '',
        shipDetails: []
    });
    const [details, setDetails] = useState({
        data: {
            id: 0,
            name: '',
            tid: '',
            template_name: '',
            dids: [],
            target_size: 0,
            apply_if: 'earlier build date',
            preffered_time: 'any',
            host: '',
            file: '',
            file_size: '',
            md5_checksum: '',
            firmware: '',
            type: '',
            build: '',
            attempts_limit: 3,
            attempts_reset_period: 24,
            attempts_reset_period_unit: 'hours',
            notification: false,
            start_date: '',
            status: ''
        }, loading: true, opened: false
    });
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const { lang } = useContext(LanguageContext);
    const t = shipmentTranslations[lang].shipmentDetails;
    const isRtl = ['ar', 'ur'].includes(lang);

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = useCallback((type, message) => {
        setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
    }, [isRtl]);

    const goToDevice = async (did) => {
        if (!did || did.trim() === '') {
            showAlert('warning', 'Device ID cannot be empty.');
            return;
        }

        try {
            const res = await axios.get(`${BASE_URL_API}/device/get-did/${did}`);

            const deviceId = res.data.data.id;

            if (deviceId) {
                navigate(`/devices/details/${deviceId}`);
            } else {
                throw new Error("Device ID was not found in the server response.");
            }

        } catch (err) {
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
        }
    };

    const showShipDetails = (id) => {
        const selectedRow = rows.find(row => row.id === id);
        setStatus({
            open: true,
            name: selectedRow.device_name,
            shipDetails: selectedRow.ship_details
        });
    };

    const fetchData = useCallback(async () => {
        if (!id) {
            setLoading(false);
            return;
        }
        setLoading(true);

        try {
            // Your efficient parallel API call logic is preserved as it's already optimal.
            const [detailsRes, statusesRes] = await Promise.all([
                axios.get(`${OTA_URL}/shipment/get/${id}`),
                axios.get(`${OTA_URL}/status/list/${id}`)
            ]);

            // The success checks are perfectly aligned with your backend's response structure.
            if (detailsRes.data && detailsRes.data.status === 'OK') {
                setDetails(prev => ({ ...prev, data: detailsRes.data.data }));
            } else {
                throw new Error('Failed to retrieve shipment details from server.');
            }

            if (statusesRes.data && statusesRes.data.status === 'OK') {
                setRows(Array.isArray(statusesRes.data.data) ? statusesRes.data.data : []);
            } else {
                throw new Error('Failed to retrieve device status list from server.');
            }

        } catch (err) {
            console.error("Fetch Data Error:", err);
            // The error handling correctly uses showAlert and resets state.
            // It will properly display messages from your backend's ApiError responses (e.g., 404, 400).
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
            setDetails({ data: null, opened: false });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [id, showAlert]);

    const actions = [
        permissions['stop_pause_shipments'] ? {
            title: t.pause,
            icon: <PauseIcon />,
            action: (id) => controlShipment(id, 'pause', fetchData)
        } : null,
        permissions['stop_pause_shipments'] ? {
            title: t.resume,
            icon: <ResumeIcon />,
            action: (id) => controlShipment(id, 'resume', fetchData)
        } : null,
        {
            title: t.details,
            icon: <InfoIcon />,
            action: (id) => setDetails({ ...details, opened: true, data: { ...details.data, id: id } })
        },
        // {
        //     title: 'Download Report',
        //     icon: <ReportIcon />,
        //     action: () => { }
        // },
        {
            title: t.cancel,
            icon: <CancelIcon style={{ color: 'rgb(236, 69, 78)' }} />,
            action: (id) => controlShipment(id, 'cancel', fetchData)
        },
        {
            title: t.delete,
            icon: <DeleteIcon color={'rgb(236, 69, 78)'} />,
            action: (id, firmware) => deleteShipment(id, firmware, () => navigate('/configuration/ota'), showAlert)
        },
    ].filter(action => action);
    /*
    const colors = {
        headerBackground: 'var(--header-background)',
        headerColor: 'var(--header-color)',
        headerHover: 'var(--header-hover)',
        cellBackground: 'var(--cell-background)',
        cellColor: 'var(--cell-color)',
        cellHover: 'var(--cell-hover)',
        orderColor: 'var(--order-color)',
        orderSelectedColor: 'rgb(49, 199, 149)',
        checkboxSelection: 'var(--accent)',
        btnBackground: 'rgb(219, 245, 236)',
        btnColor: 'var(--accent)',
        modalBorder: 'var(--modal-border)',
        red: 'rgb(236, 69, 78)'
    }
    */
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const [selectedRows, setSelectedRows] = useState([]);
    const handleRowClick = (params) => {
    };
    const pagingoptions = [10, 20, 50, 100]
    const columns = [
        { field: 'id', headerName: t.columns.id, width: 200, align: 'center', headerAlign: 'center' },
        { field: 'device_name', headerName: t.columns.deviceName, width: 200, align: 'center', headerAlign: 'center' },
        { field: 'status', headerName: t.columns.status, width: 200, align: 'center', headerAlign: 'center' },
        { field: 'state', headerName: t.columns.state, width: 100, type: 'number', align: 'center', headerAlign: 'center' },
        { field: 'attempts', headerName: t.columns.attempts, width: 100, type: 'number', align: 'center', headerAlign: 'center' },
        { field: 'ship_status', headerName: t.columns.shipStatus, width: 100, type: 'number', align: 'center', headerAlign: 'center' },
        { field: 'organization', headerName: t.columns.organization, width: 300, align: 'center', headerAlign: 'center' },
        { field: 'firmware_updated_on', headerName: t.columns.firmwareUpdate, width: 200, align: 'center', headerAlign: 'center' },
        {
            field: 'actions', headerName: t.columns.actions, type: 'actions', width: 100,
            getActions: (params) => [
                <GridActionsCellItem icon={<Tooltip title={t.openDetails}><InfoIcon /></Tooltip>} showInMenu label={t.openDetails} onClick={() => showShipDetails(params.row.id)} />,
                <GridActionsCellItem icon={<Tooltip title={t.openDevice}><DeviceIcon /></Tooltip>} showInMenu label={t.openDevice} onClick={() => goToDevice(params.row.did)} />
            ]
        },
    ];

    return (details.loading ? <Spinner />
        : permissions['firmware_get_access'] ?
            <div className='details-container'>
                <Alert
                    open={alertState.open}
                    close={() => setAlertState({ ...alertState, open: false })}
                    message={alertState.message}
                    type={alertState.type}
                    position={alertState.position}
                />
                {details.opened ? <ShipmentInfo initialData={details.data}
                    state={{ opened: details.opened, id: details.data.id }} setState={setDetails} /> : null}
                <div className='details-control-panel'>
                    <span className='details-control-btn'
                        onClick={() => navigate('/configuration/ota')}>
                        <small>{'\u2715'}</small>
                    </span>
                    {/* <span className='details-control-btn'>
                    <small>{'\u2630'}</small>
                </span> */}
                </div>
                <div className='details-content' style={{ padding: 0 }}>
                    <div className='details-remain-head-part'
                        style={{ borderBottom: '1px solid var(--header-hover)', padding: 24 }}>
                        <h1>{details.data.name}</h1>
                        <div style={{ position: 'relative' }}>
                            <div className='details-status-badge' onMouseEnter={() => {
                                const menu = document.querySelector('.details-actions-menu.status');
                                menu.style.display = 'block';
                            }} onMouseLeave={(e) => {
                                const menu = document.querySelector('.details-actions-menu.status'),
                                    rect = e.target.getBoundingClientRect(),
                                    top = parseInt(rect.top), left = parseInt(rect.left), right = parseInt(rect.right),
                                    x = parseInt(e.clientX), y = parseInt(e.clientY);

                                if (y < top || x < left || x > right) {
                                    menu.style.display = 'none';
                                }
                            }}>{details.data.status}</div>
                            <div className='details-actions-menu status'
                                style={{ zIndex: 4, width: 320 }} onMouseLeave={() => {
                                    const menu = document.querySelector('.details-actions-menu.status');
                                    menu.style.display = 'none';
                                }}>
                                <div className='details-actions-menu-item'>Target: {details.data.target_size}</div>
                                <div className='details-actions-menu-item'>Build: {details.data.build}</div>
                            </div>
                        </div>
                        <div style={{ position: 'relative' }}>
                            <button className='details-actions-btn' onMouseEnter={() => {
                                const menu = document.querySelector('.details-actions-menu.actions');
                                menu.style.display = 'block';
                            }} onMouseLeave={(e) => {
                                const menu = document.querySelector('.details-actions-menu.actions'),
                                    rect = e.target.getBoundingClientRect(),
                                    top = parseInt(rect.top), left = parseInt(rect.left), right = parseInt(rect.right),
                                    x = parseInt(e.clientX), y = parseInt(e.clientY);

                                if (y < top || x < left || x > right) {
                                    menu.style.display = 'none';
                                }
                            }}>
                                <ActionsIcon color={'var(--accent)'} />
                            </button>
                            <div style={{ zIndex: 3 }} className='details-actions-menu actions' onMouseLeave={() => {
                                const menu = document.querySelector('.details-actions-menu.actions');
                                menu.style.display = 'none';
                            }}>
                                {actions.map((action, idx) => (
                                    <div key={idx} className='details-actions-menu-item'
                                        onClick={() => action.action(id)}>
                                        {action.icon}
                                        <span style={{
                                            color: action.title === 'Delete' || action.title === 'Cancel' ? 'rgb(236, 69, 78)' : ''
                                        }}>{action.title}</span>
                                    </div>))}
                            </div>
                        </div>
                    </div>
                    <div className='details-body' style={{ paddingInlineStart: 24 }}>
                        <ShipmentStatus state={status}
                            setState={setStatus} />
                        {loading ? <Spinner />
                            :
                            <div className="table-wrapper" style={{ flex: 1, minHeight: 0 }}>
                                <DataGridTable columns={columns} rows={rows} idcolumn={"id"} pagingoptions={pagingoptions}
                                    handleRowClick={handleRowClick}
                                    checkboxSelection={true}
                                    rowSelectionModel={selectedRows} setRowSelectionModel={setSelectedRows}
                                    showToolbar={true}
                                    tableKey='shipmentDetails'>
                                </DataGridTable>
                            </div>
                        }
                    </div>
                </div>
            </div> : <div>{t.noPermission}</div>
    );
};

